import { useEffect, useRef, useCallback } from 'react'
import {
  UICommandType,
  UICommandPayload,
  UIStateKey,
  ProjectOpenData,
  ProjectCloseData,
  ProjectEditorOpenData,
  TerminalFocusData,
  FileHighlightData,
  DevPreviewOpenData,
  ArchitectureUpdateData,
  FsChangesPayload,
  SessionStreamingData,
  SessionStreamingSnapshotData,
  TerminalExecutionPayload,
  NODE_PROTOCOL_VERSION,
} from '@jait/shared'
import { desktopOperationError } from '@/lib/desktop-operation-error'
import { applyTerminalExecutionEvent } from '@/lib/tool-terminal-live'

import { Filesystem, Directory } from '@capacitor/filesystem'
import { getWsUrl } from '@/lib/gateway-url'
import { detectPlatform, initDeviceId } from '@/lib/device-id'
import {
  revokeSystemNotification,
  setNativeNotificationsEnabled,
  triggerSystemNotification,
  type SystemNotificationInput,
} from '@/lib/system-notifications'

const WS_URL = getWsUrl()

/** Maps the build target onto the gateway's `ClientSurface` vocabulary. */
function clientSurface(): 'desktop' | 'mobile' | 'web' {
  const platform = detectPlatform()
  if (platform === 'desktop') return 'desktop'
  if (platform === 'capacitor') return 'mobile'
  return 'web'
}

// Tools this desktop node can execute remotely. Project-scoped tools stay in
// sync with REMOTE_EXECUTABLE_TOOLS; computer.* tools are addressed directly
// to an explicitly selected node by the gateway computer-control tools.
const DESKTOP_NODE_TOOLS = [
  'terminal.run',
  'jait.terminal',
  'execute',
  'read',
  'file.read',
  'edit',
  'file.write',
  'file.patch',
  'file.list',
  'file.stat',
  'image.view',
  'search',
  'file.search',
  'os.query',
  'computer.session',
  'computer.observe',
  'computer.act',
]

// ── Device / platform helpers ───────────────────────────────────────

function getDeviceName(): string {
  const platform = detectPlatform()
  const ua = navigator.userAgent
  if (platform === 'desktop') return `Desktop (${navigator.platform})`
  if (platform === 'capacitor') return 'Mobile'
  if (ua.includes('Chrome')) return `Chrome (${navigator.platform})`
  if (ua.includes('Firefox')) return `Firefox (${navigator.platform})`
  if (ua.includes('Safari')) return `Safari (${navigator.platform})`
  return `Browser (${navigator.platform})`
}

export function resolveFsNodePlatform(
  appPlatform: ReturnType<typeof detectPlatform>,
  nativePlatform: string | null | undefined,
  browserPlatform: string,
): string {
  if (appPlatform === 'capacitor') return 'android' // or ios, but we'll keep it simple
  if (appPlatform === 'desktop') {
    const plat = (nativePlatform || browserPlatform).toLowerCase()
    if (plat === 'win32' || plat === 'windows' || plat.includes('win')) return 'windows'
    if (plat === 'darwin' || plat === 'macos' || plat.includes('mac')) return 'macos'
    return 'linux'
  }
  return 'web'
}

function detectFsNodePlatform(): string {
  return resolveFsNodePlatform(
    detectPlatform(),
    window.jaitDesktop?.nativePlatform,
    navigator.platform ?? '',
  )
}

/**
 * Whether this client can act as a filesystem node (browse local files).
 * Browser clients served from a local dev server can't really expose files,
 * but Desktop and Capacitor can.
 */
function canActAsFsNode(): boolean {
  const p = detectPlatform()
  return p === 'desktop' || p === 'capacitor'
}

// ── Listener map ────────────────────────────────────────────────────
type CommandDataMap = {
  'project.open': ProjectOpenData
  'project.close': ProjectCloseData
  'project.editor.open': ProjectEditorOpenData
  'terminal.focus': TerminalFocusData
  'file.highlight': FileHighlightData
  'dev-preview.open': DevPreviewOpenData
  'architecture.update': ArchitectureUpdateData
}

type UICommandListener<T extends UICommandType = UICommandType> =
  T extends keyof CommandDataMap ? (data: CommandDataMap[T]) => void : (data: Record<string, unknown>) => void

interface Listeners {
  'project.open'?: UICommandListener<'project.open'>
  'project.close'?: UICommandListener<'project.close'>
  'project.editor.open'?: UICommandListener<'project.editor.open'>
  'terminal.focus'?: UICommandListener<'terminal.focus'>
  'file.highlight'?: UICommandListener<'file.highlight'>
  'dev-preview.open'?: UICommandListener<'dev-preview.open'>
  'architecture.update'?: UICommandListener<'architecture.update'>
  'screen-share.open'?: (data: Record<string, unknown>) => void
  'screen-share.close'?: (data: Record<string, unknown>) => void
}

/**
 * Callback for `ui.state-sync` events from other clients / the gateway.
 * `key` is the state key (e.g. "project.panel", "todo_list", "file_changed").
 */
export type StateSyncHandler = (key: string, value: unknown) => void

/**
 * Callback for `ui.full-state` — the complete session state pushed on subscribe.
 */
export type FullStateHandler = (state: Record<string, unknown>) => void

export function shouldApplySessionScopedWsEvent(
  eventSessionId: unknown,
  activeSessionId: string | null | undefined,
): boolean {
  return typeof eventSessionId === 'string' && eventSessionId === activeSessionId
}

/**
 * Callback for thread-related WS events (thread.created, thread.updated, etc.).
 * `eventType` is the full WS event type (e.g. "thread.created").
 * `payload` is the event payload (e.g. { threadId, thread }).
 */
export type ThreadEventHandler = (eventType: string, payload: Record<string, unknown>) => void
export type UICommandsConnectionStateHandler = (state: { connected: boolean; reconnected: boolean }) => void

interface UseUICommandsOptions {
  /** Listeners for UI commands pushed by the gateway (server → client). */
  listeners: Listeners
  /** Active session ID — used to subscribe to session-scoped broadcasts. */
  sessionId?: string | null
  /** Token for WS authentication. */
  token?: string | null
  /** Called when another client (or the gateway) broadcasts a state change. */
  onStateSync?: StateSyncHandler
  /** Called when the gateway pushes the full session state on subscribe/reconnect. */
  onFullState?: FullStateHandler
  /** Called when the gateway broadcasts that an assistant message started outside this client's direct request. */
  onMessageStarted?: () => void
  /** Called when the gateway broadcasts that an assistant message has completed. */
  onMessageComplete?: () => void
  /** Called when any of the user's sessions starts/stops streaming a response, regardless of which session is currently open. */
  onSessionStreamingChange?: (sessionId: string, streaming: boolean) => void
  /** Called with the authoritative running-session set after every WebSocket connection. */
  onSessionStreamingSnapshot?: (sessionIds: string[]) => void
  /** Called when the gateway broadcasts a thread lifecycle event. */
  onThreadEvent?: ThreadEventHandler
  /** Called when the gateway broadcasts preview session state changes. */
  onPreviewSessionEvent?: (session: Record<string, unknown>) => void
  /** Called when the gateway pushes native filesystem change events. */
  onFsChanges?: (payload: FsChangesPayload) => void
  /** Called when the UI command WebSocket connects, disconnects, or reconnects. */
  onConnectionStateChange?: UICommandsConnectionStateHandler
}

/**
 * Subscribe to backend-pushed UI commands over WebSocket,
 * and expose a `sendUIState` function for client → server state sync.
 *
 * The gateway sends `{ type: "ui.command", payload: { command, data } }`
 * and this hook dispatches to the matching listener callback.
 *
 * On subscribe, the gateway pushes `{ type: "ui.full-state", payload: Record<string, unknown> }`
 * with the complete session state from the DB — this is the authoritative state.
 *
 * `sendUIState(key, value, sessionId)` pushes a `ui.state` message to the
 * gateway which persists it in the session_state DB table and relays
 * to other clients via `ui.state-sync`.
 */
export function useUICommands(opts: UseUICommandsOptions) {
  const {
    listeners,
    sessionId,
    token,
    onStateSync,
    onFullState,
    onMessageStarted,
    onMessageComplete,
    onSessionStreamingChange,
    onSessionStreamingSnapshot,
    onThreadEvent,
    onFsChanges,
    onConnectionStateChange,
    onPreviewSessionEvent,
  } = opts
  const listenersRef = useRef(listeners)
  listenersRef.current = listeners
  const onStateSyncRef = useRef(onStateSync)
  onStateSyncRef.current = onStateSync
  const onFullStateRef = useRef(onFullState)
  onFullStateRef.current = onFullState
  const onMessageStartedRef = useRef(onMessageStarted)
  onMessageStartedRef.current = onMessageStarted
  const onMessageCompleteRef = useRef(onMessageComplete)
  onMessageCompleteRef.current = onMessageComplete
  const onSessionStreamingChangeRef = useRef(onSessionStreamingChange)
  onSessionStreamingChangeRef.current = onSessionStreamingChange
  const onSessionStreamingSnapshotRef = useRef(onSessionStreamingSnapshot)
  onSessionStreamingSnapshotRef.current = onSessionStreamingSnapshot
  const onThreadEventRef = useRef(onThreadEvent)
  onThreadEventRef.current = onThreadEvent
  const onPreviewSessionEventRef = useRef(onPreviewSessionEvent)
  onPreviewSessionEventRef.current = onPreviewSessionEvent
  const onFsChangesRef = useRef(onFsChanges)
  onFsChangesRef.current = onFsChanges
  const onConnectionStateChangeRef = useRef(onConnectionStateChange)
  onConnectionStateChangeRef.current = onConnectionStateChange
  const wsRef = useRef<WebSocket | null>(null)
  const currentSessionRef = useRef<string | null>(null)
  const mountedRef = useRef(true)
  const hasConnectedRef = useRef(false)
  const tokenRef = useRef(token)
  tokenRef.current = token
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  // Queue for messages that couldn't be sent because WS was not open
  const outgoingQueueRef = useRef<string[]>([])

  // Flush queued messages when WS becomes ready
  const flushQueue = useCallback((ws: WebSocket) => {
    while (outgoingQueueRef.current.length > 0 && ws.readyState === WebSocket.OPEN) {
      const msg = outgoingQueueRef.current.shift()!
      ws.send(msg)
    }
  }, [])

  // Subscribe (or re-subscribe) the WS to a session
  const subscribeToSession = useCallback((ws: WebSocket, sid: string | null) => {
    if (!sid || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'subscribe', sessionId: sid }))
    currentSessionRef.current = sid
  }, [])

  // ── Cross-platform notification handler ──────────────────────────

  const handleGatewayNotification = useCallback(async (notif: SystemNotificationInput) => {
    await triggerSystemNotification({ ...notif, includeToast: notif.includeToast ?? notif.kind !== 'completion' })
  }, [])

  // Handle incoming messages — extracted so it's stable across reconnects
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data) as { type: string; sessionId?: unknown; payload: unknown }

      if (msg.type === 'ui.command') {
        const payload = msg.payload as UICommandPayload
        const handler = listenersRef.current[payload.command as keyof Listeners]
        if (handler) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(handler as (data: any) => void)(payload.data)
        }
        // Agent-driven Email page control — decoupled from the central listener
        // map so the Email page can react wherever it is mounted.
        if (payload.command === 'email.control' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('jait:email-control', { detail: payload.data }))
        }
      } else if (msg.type === 'ui.state-sync') {
        // Cross-client state sync from another client or the gateway
        const payload = msg.payload as { key?: string; value?: unknown }
        if (
          shouldApplySessionScopedWsEvent(msg.sessionId, sessionIdRef.current)
          && payload?.key
          && onStateSyncRef.current
        ) {
          onStateSyncRef.current(payload.key, payload.value ?? null)
        }
      } else if (msg.type === 'ui.full-state') {
        // Full session state pushed by the gateway on subscribe — authoritative
        const state = msg.payload as Record<string, unknown> | null
        if (
          shouldApplySessionScopedWsEvent(msg.sessionId, sessionIdRef.current)
          && state
          && onFullStateRef.current
        ) {
          onFullStateRef.current(state)
        }
      } else if (msg.type === 'message.started') {
        // Assistant message started outside this client's direct request — attach to the live stream.
        if (shouldApplySessionScopedWsEvent(msg.sessionId, sessionIdRef.current)) {
          onMessageStartedRef.current?.()
        }
      } else if (msg.type === 'message.complete') {
        // Assistant message finished on another device — refresh chat
        if (shouldApplySessionScopedWsEvent(msg.sessionId, sessionIdRef.current)) {
          onMessageCompleteRef.current?.()
        }
      } else if (msg.type === 'session.streaming') {
        // Any of the user's sessions started/stopped streaming — sidebar-wide, not session-scoped
        const payload = msg.payload as SessionStreamingData
        onSessionStreamingChangeRef.current?.(payload.sessionId, payload.streaming)
      } else if (msg.type === 'session.streaming-snapshot') {
        const payload = msg.payload as SessionStreamingSnapshotData
        onSessionStreamingSnapshotRef.current?.(payload.sessionIds)
      } else if (msg.type === 'terminal.execution') {
        // A terminal tool call claimed (or released) a terminal. Recorded
        // globally rather than routed through a listener: the running tool card
        // reads it straight out of the store to attach its live terminal, and it
        // may belong to a sub-agent session this socket is not subscribed to.
        applyTerminalExecutionEvent(
          typeof msg.sessionId === 'string' ? msg.sessionId : '',
          msg.payload as TerminalExecutionPayload,
        )
      } else if (msg.type === 'fs.changes') {
        // Native filesystem change events from the project watcher
        const payload = msg.payload as FsChangesPayload
        onFsChangesRef.current?.(payload)
      } else if (msg.type === 'fs.browse-request') {
        // Gateway is asking us to browse a local directory
        void handleFsBrowseRequest(msg.payload as { requestId: string; path: string })
      } else if (msg.type === 'fs.roots-request') {
        // Gateway is asking for our root directories
        void handleFsRootsRequest(msg.payload as { requestId: string })
      } else if (msg.type === 'fs.op-request') {
        // Gateway is asking us to perform a filesystem operation (stat, read, write, list, etc.)
        void handleFsOpRequest(msg.payload as { requestId: string; op: string; [key: string]: unknown })
      } else if (msg.type === 'provider.op-request') {
        // Gateway is asking us to run a provider operation (start-session, send-turn, etc.)
        void handleProviderOpRequest(msg.payload as { requestId: string; op: string; [key: string]: unknown })
      } else if (msg.type === 'tool.op-request') {
        // Gateway is asking us to execute a Jait tool locally (terminal.run, file.write, etc.)
        void handleToolOpRequest(msg.payload as { requestId: string; tool: string; args: Record<string, unknown>; sessionId?: string; projectRoot?: string; backgroundId?: string })
      } else if (msg.type === 'terminal.op-request') {
        // Gateway is asking us to run an interactive terminal operation on this node.
        void handleTerminalOpRequest(msg.payload as { requestId?: string; op: string; [key: string]: unknown })
      } else if (msg.type === 'notification') {
        // Cross-platform notification from the gateway
        void handleGatewayNotification(msg.payload as {
          id: string; title: string; body: string; level: 'info' | 'success' | 'warning' | 'error'; link?: string
        })
      } else if (msg.type === 'attention.raised') {
        // The gateway decides which surface owns the OS notification for this
        // machine. When Desktop is connected it takes the toast and this tab
        // shows the in-app prompt only, so one request never pops twice.
        const attention = msg.payload as { native?: boolean }
        setNativeNotificationsEnabled(attention.native !== false)
      } else if (msg.type === 'attention.cleared') {
        // Answered elsewhere — pull down whatever this client already showed.
        const cleared = msg.payload as { key?: string }
        if (cleared.key) void revokeSystemNotification(cleared.key)
      } else if (
        msg.type.startsWith('thread.') ||
        msg.type.startsWith('repo.') ||
        msg.type.startsWith('plan.') ||
        msg.type.startsWith('fs.node-') ||
        msg.type.startsWith('node.') ||
        msg.type.startsWith('project.') ||
        msg.type.startsWith('chat.')
      ) {
        // Thread, repo, plan, filesystem-node, node-registry, project and chat
        // events — forward to automation hook
        onThreadEventRef.current?.(msg.type, msg.payload as Record<string, unknown>)
      } else if (msg.type === 'preview.session') {
        const session = (msg.payload as Record<string, unknown>)?.session as Record<string, unknown> | undefined
        if (session) onPreviewSessionEventRef.current?.(session)
      }
    } catch {
      // ignore parse errors
    }
  }, [])

  // ── Filesystem node request handlers ──────────────────────────────

  /** Browse a local directory using Capacitor Filesystem API */
  const capacitorBrowse = useCallback(async (dirPath: string) => {
    // Determine the base directory and relative path
    let directory: typeof Directory[keyof typeof Directory] | undefined
    let path = dirPath
    if (dirPath === '~' || dirPath === '/storage' || dirPath === '/') {
      // Root request — list the external storage root
      directory = Directory.ExternalStorage
      path = ''
    } else if (dirPath.startsWith('/storage/emulated/0')) {
      directory = Directory.ExternalStorage
      path = dirPath.replace('/storage/emulated/0', '').replace(/^\//, '')
    }
    const result = await Filesystem.readdir({
      path: path || '',
      ...(directory ? { directory } : {}),
    })
    const basePath = directory === Directory.ExternalStorage
      ? '/storage/emulated/0' + (path ? '/' + path : '')
      : dirPath
    const entries: { name: string; path: string; type: 'dir' | 'file' }[] = []
    for (const f of result.files) {
      if (f.name.startsWith('.')) continue
      entries.push({
        name: f.name,
        path: basePath + '/' + f.name,
        type: f.type === 'directory' ? 'dir' : 'file',
      })
    }
    entries.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'dir' ? -1 : 1
      return a.name.localeCompare(b.name)
    })
    // Compute parent
    const parts = basePath.replace(/\/+$/, '').split('/')
    const parent = parts.length > 3 ? parts.slice(0, -1).join('/') : null
    return { path: basePath, parent, entries }
  }, [])

  /** Get root directories on Capacitor (Android) */
  const capacitorRoots = useCallback(async () => {
    return [
      { name: 'Internal Storage', path: '/storage/emulated/0', type: 'dir' as const },
      { name: 'Documents', path: '/storage/emulated/0/Documents', type: 'dir' as const },
      { name: 'Downloads', path: '/storage/emulated/0/Download', type: 'dir' as const },
      { name: 'Home', path: '/storage/emulated/0', type: 'dir' as const },
    ]
  }, [])

  /** Respond to a remote browse request from the gateway */
  const handleFsBrowseRequest = useCallback(async (payload: { requestId: string; path: string }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const { requestId, path } = payload
    try {
      let result: { path: string; parent: string | null; entries: { name: string; path: string; type: 'dir' | 'file' }[] }
      const platform = detectPlatform()
      if (platform === 'desktop' && window.jaitDesktop?.browsePath) {
        result = await window.jaitDesktop.browsePath(path)
      } else if (platform === 'capacitor') {
        result = await capacitorBrowse(path)
      } else {
        throw new Error('Local filesystem browsing not supported on this platform')
      }
      ws.send(JSON.stringify({
        type: 'fs.browse-response',
        payload: {
          requestId,
          path: result.path,
          parent: result.parent,
          entries: result.entries,
        },
      }))
    } catch (err) {
      ws.send(JSON.stringify({
        type: 'fs.browse-response',
        payload: {
          requestId,
          error: err instanceof Error ? err.message : 'Browse failed',
        },
      }))
    }
  }, [capacitorBrowse])

  /** Respond to a remote roots request from the gateway */
  const handleFsRootsRequest = useCallback(async (payload: { requestId: string }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const { requestId } = payload
    try {
      let roots: { name: string; path: string; type: 'dir' | 'file' }[]
      const platform = detectPlatform()
      if (platform === 'desktop' && window.jaitDesktop?.getRoots) {
        const result = await window.jaitDesktop.getRoots()
        roots = result.roots
      } else if (platform === 'capacitor') {
        roots = await capacitorRoots()
      } else {
        throw new Error('Local filesystem browsing not supported on this platform')
      }
      ws.send(JSON.stringify({
        type: 'fs.roots-response',
        payload: { requestId, roots },
      }))
    } catch (err) {
      ws.send(JSON.stringify({
        type: 'fs.roots-response',
        payload: {
          requestId,
          error: err instanceof Error ? err.message : 'Roots request failed',
        },
      }))
    }
  }, [capacitorRoots])

  /**
   * Handle a generic filesystem operation request from the gateway.
   * Operations: stat, read, write, list, exists, mkdir, readdir
   * Each is dispatched to the Desktop IPC bridge (or Capacitor on mobile).
   */
  const handleFsOpRequest = useCallback(async (payload: { requestId: string; op: string; [key: string]: unknown }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const { requestId, op, ...params } = payload
    try {
      const platform = detectPlatform()
      if (platform === 'desktop' && window.jaitDesktop?.fsOp) {
        const result = await window.jaitDesktop.fsOp(op, params)
        ws.send(JSON.stringify({
          type: 'fs.op-response',
          payload: { requestId, result },
        }))
      } else if (platform === 'capacitor') {
        // For now, capacitor doesn't support full fs operations
        throw new Error('Full filesystem operations not yet supported on mobile')
      } else {
        throw new Error('Filesystem operations not supported on this platform')
      }
    } catch (err) {
      ws.send(JSON.stringify({
        type: 'fs.op-response',
        payload: {
          requestId,
          error: err instanceof Error ? err.message : 'Filesystem operation failed',
        },
      }))
    }
  }, [])

  /**
   * Handle a provider operation request from the gateway.
   * Operations: start-session, send-turn, stop-session, list-models
   * Dispatches to the Desktop IPC bridge.
   */
  const handleProviderOpRequest = useCallback(async (payload: { requestId: string; op: string; [key: string]: unknown }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const { requestId, op, ...params } = payload
    try {
      const platform = detectPlatform()
      if (platform === 'desktop' && window.jaitDesktop?.providerOp) {
        const result = await window.jaitDesktop.providerOp(op, params)
        ws.send(JSON.stringify({
          type: 'provider.op-response',
          payload: { requestId, result },
        }))
      } else {
        throw new Error('Provider operations not supported on this platform')
      }
    } catch (err) {
      ws.send(JSON.stringify({
        type: 'provider.op-response',
        payload: {
          requestId,
          error: desktopOperationError(err, 'Provider operation failed'),
        },
      }))
    }
  }, [])

  /**
   * Handle a tool execution request from the gateway.
   * Dispatches to the Desktop IPC bridge for local execution on this node.
   */
  const handleToolOpRequest = useCallback(async (payload: {
    requestId: string; tool: string; args: Record<string, unknown>;
    sessionId?: string; projectRoot?: string; backgroundId?: string
  }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const { requestId, tool, args, sessionId, projectRoot, backgroundId } = payload
    try {
      const platform = detectPlatform()
      if (platform === 'desktop' && window.jaitDesktop?.toolOp) {
        const result = await window.jaitDesktop.toolOp(
          tool,
          args,
          { sessionId, projectRoot, backgroundId },
        )
        ws.send(JSON.stringify({
          type: 'tool.op-response',
          payload: { requestId, result },
        }))
      } else {
        throw new Error('Tool execution not supported on this platform')
      }
    } catch (err) {
      ws.send(JSON.stringify({
        type: 'tool.op-response',
        payload: {
          requestId,
          error: desktopOperationError(err, 'Tool execution failed'),
        },
      }))
    }
  }, [])

  const handleTerminalOpRequest = useCallback(async (payload: { requestId?: string; op: string; [key: string]: unknown }) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const { requestId, op, ...params } = payload
    try {
      const platform = detectPlatform()
      if (platform === 'desktop' && window.jaitDesktop?.terminalOp) {
        const result = await window.jaitDesktop.terminalOp(op, params)
        if (requestId && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'terminal.op-response',
            payload: { requestId, result },
          }))
        }
      } else {
        throw new Error('Interactive terminals are not supported on this platform')
      }
    } catch (err) {
      if (requestId && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'terminal.op-response',
          payload: {
            requestId,
            error: err instanceof Error ? err.message : 'Terminal operation failed',
          },
        }))
      }
    }
  }, [])

  // ── Single, stable WS connection — only depends on token ──────────
  // Session changes are handled by re-subscribing, NOT by reconnecting.
  useEffect(() => {
    // Don't open a WebSocket until the user is authenticated.
    // Without this guard the hook reconnects every 1 s during the auth gate,
    // causing state churn and drag-lag on Windows/Desktop.
    if (!token) return

    mountedRef.current = true
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let pausedForHiddenDocument = false

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
    }

    const closeSocket = () => {
      const ws = wsRef.current
      if (!ws) return
      ws.onclose = null
      ws.close()
      wsRef.current = null
      currentSessionRef.current = null
    }

    // Desktop desktops act as always-on nodes and must keep their gateway
    // WebSocket alive even when the window is hidden to the tray. Only
    // browsers/capacitor pause (and later reconnect) on visibility.
    const isAlwaysOnNode = detectPlatform() === 'desktop'

    const connect = () => {
      if (!mountedRef.current) return
      // Always-on (Desktop) nodes stay connected in the background; only
      // browsers/capacitor skip connecting while the tab/window is hidden.
      if (!isAlwaysOnNode && typeof document !== 'undefined' && document.hidden) return
      // Declare the surface so the gateway's attention fan-out can decide who
      // owns the OS notification — an Desktop window and a browser tab on the
      // same machine are two clients but one human.
      const ws = new WebSocket(
        `${WS_URL}?token=${tokenRef.current ?? 'dev'}&surface=${clientSurface()}`,
      )
      wsRef.current = ws

      ws.onopen = () => {
        const reconnected = hasConnectedRef.current
        hasConnectedRef.current = true
        onConnectionStateChangeRef.current?.({ connected: true, reconnected })
        ws.send(JSON.stringify({
          type: 'resource.subscribe',
          payload: { resource: 'root:/nodes' },
        }))
        ws.send(JSON.stringify({
          type: 'resource.subscribe',
          payload: { resource: 'root:/threads' },
        }))
        ws.send(JSON.stringify({
          type: 'resource.subscribe',
          payload: { resource: 'root:/streaming-sessions' },
        }))
        ws.send(JSON.stringify({
          type: 'resource.subscribe',
          payload: { resource: 'root:/surfaces' },
        }))
        ws.send(JSON.stringify({
          type: 'resource.subscribe',
          payload: { resource: 'root:/browser' },
        }))
        // Flush pending state before subscribing. Otherwise the server can send
        // a stale full-state snapshot that clears a locally queued chat message
        // before its delayed state update reaches the gateway.
        flushQueue(ws)
        // Subscribe to current session only after pending state is authoritative.
        const sid = sessionIdRef.current
        if (sid) subscribeToSession(ws, sid)
        // Register as a filesystem node if this client can browse files locally
        if (canActAsFsNode()) {
          // Ensure device ID is initialised from persistent storage
          void initDeviceId().then(async (deviceId) => {
            // Detect locally installed CLI providers and whether the desktop's
            // own CLI authentication is ready for remote execution.
            let providerStatuses: Array<{ id: string; installed: boolean; authenticated: boolean | null; detail?: string }> = []
            if (detectPlatform() === 'desktop' && window.jaitDesktop?.detectProviders) {
              try {
                const detectedProviders = await window.jaitDesktop.detectProviders()
                providerStatuses = detectedProviders.map((provider) => (
                  typeof provider === 'string'
                    ? { id: provider, installed: true, authenticated: null }
                    : provider
                ))
              } catch { /* */ }
            }
            const providers = providerStatuses
              .filter((provider) => provider.installed && provider.authenticated !== false)
              .map((provider) => provider.id)
            const nodeMsg = JSON.stringify({
              type: 'node.hello',
              payload: {
                id: deviceId,
                name: getDeviceName(),
                platform: detectFsNodePlatform(),
                role: detectPlatform() === 'desktop' ? 'desktop' : 'mobile',
                protocolVersion: NODE_PROTOCOL_VERSION,
                capabilities: {
                  providers,
                  surfaces: ['filesystem', 'terminal', 'computer'],
                  tools: DESKTOP_NODE_TOOLS,
                  screenShare: true,
                  voice: false,
                  preview: false,
                  // Only claim interactive-terminal RPC support when the desktop
                  // bridge actually exposes terminalOp. Older builds register the
                  // "terminal" surface (subscribe/output) but never answer
                  // terminal.op-request, which would otherwise hang the gateway.
                  interactiveTerminal: detectPlatform() === 'desktop' && !!window.jaitDesktop?.terminalOp,
                },
              },
            })
            if (ws.readyState === WebSocket.OPEN) ws.send(nodeMsg)
            const fsNodeMsg = JSON.stringify({
              type: 'fs.register-node',
              payload: {
                id: deviceId,
                name: getDeviceName(),
                platform: detectFsNodePlatform(),
                providers,
                providerStatuses,
              },
            })
            if (ws.readyState === WebSocket.OPEN) ws.send(fsNodeMsg)
          })
        }
      }

      ws.onmessage = handleMessage

      ws.onclose = () => {
        wsRef.current = null
        currentSessionRef.current = null
        onConnectionStateChangeRef.current?.({ connected: false, reconnected: false })
        // Auto-reconnect after 1s
        if (mountedRef.current && !pausedForHiddenDocument) {
          reconnectTimer = setTimeout(connect, 1000)
        }
      }

      ws.onerror = () => {
        // onclose will fire after onerror, triggering reconnect
      }
    }

    // Desktop always-on nodes keep their connection live while hidden to the
    // tray; browsers/capacitor pause and reconnect on visibility changes.
    const handleVisibilityChange = () => {
      const hidden = typeof document !== 'undefined' && document.hidden
      if (isAlwaysOnNode) {
        // Desktop: keep the connection live while hidden to tray.
        // If the socket dropped while hidden, reconnect on visibility.
        if (!hidden && !wsRef.current && mountedRef.current) connect()
        return
      }
      pausedForHiddenDocument = hidden
      if (hidden) {
        clearReconnectTimer()
        closeSocket()
        return
      }
      // Force a fresh connection on resume. Mobile browsers often suspend the
      // socket in the background without firing `close` (or the close races
      // with resume), so wsRef.current can still be set here even though we
      // missed broadcasts. Reconnect unconditionally so the gateway re-pushes
      // the authoritative `session.streaming-snapshot` and the sidebar never
      // keeps a stale streaming spinner after the tab was hidden.
      closeSocket()
      connect()
    }

    connect()
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    // Set up Desktop IPC listener for provider events from child processes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gatewayEventHandler = (_event: unknown, data: any) => {
      if (data?.type === 'provider.event-from-child') {
        const msg = JSON.stringify({
          type: 'provider.event',
          payload: {
            sessionId: data.sessionId,
            event: data.notification,
          },
        })
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(msg)
        else outgoingQueueRef.current.push(msg)
      } else if (data?.type === 'terminal.output-from-child') {
        const msg = JSON.stringify({
          type: 'terminal.output',
          payload: { terminalId: data.terminalId, data: data.data },
        })
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(msg)
        else outgoingQueueRef.current.push(msg)
      } else if (data?.type === 'tool.background-complete-from-child') {
        // A background command finished on this node — the gateway is waiting
        // on this to wake the agent that started it.
        const msg = JSON.stringify({
          type: 'tool.background-complete',
          payload: { backgroundId: data.backgroundId, exitCode: data.exitCode, output: data.output },
        })
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(msg)
        else outgoingQueueRef.current.push(msg)
      } else if (data?.type === 'terminal.exit-from-child') {
        const msg = JSON.stringify({
          type: 'terminal.exit',
          payload: { terminalId: data.terminalId, exitCode: data.exitCode, signal: data.signal },
        })
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(msg)
        else outgoingQueueRef.current.push(msg)
      }
    }
    if (detectPlatform() === 'desktop' && window.jaitDesktop?.onGatewayEvent) {
      window.jaitDesktop.onGatewayEvent(gatewayEventHandler)
    }

    return () => {
      mountedRef.current = false
      clearReconnectTimer()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
      // Clean up Desktop IPC listener
      if (detectPlatform() === 'desktop' && window.jaitDesktop?.removeGatewayEventListener) {
        window.jaitDesktop.removeGatewayEventListener()
      }
      closeSocket()
    }
  // Only reconnect the WS when the auth token changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Re-subscribe when sessionId changes (no WS reconnection needed)
  useEffect(() => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN && sessionId !== currentSessionRef.current) {
      subscribeToSession(ws, sessionId ?? null)
    }
    // Also update the ref so reconnects use the latest sessionId
    sessionIdRef.current = sessionId ?? null
  }, [sessionId, subscribeToSession])

  /**
   * Send a UI state update to the gateway for DB persistence + cross-client broadcast.
   * Call this whenever the user opens/closes an agent-controllable panel.
   * Messages are queued if the WS is not currently connected.
   */
  const sendUIState = useCallback((key: UIStateKey, value: unknown | null, sid?: string | null) => {
    const msg = JSON.stringify({
      type: 'ui.state',
      payload: { sessionId: sid ?? '', key, value },
    })
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(msg)
    } else {
      // Queue for delivery when WS reconnects
      outgoingQueueRef.current.push(msg)
    }
  }, [])

  const sendArchitectureRenderResult = useCallback((requestId: string, result: { ok: true } | { ok: false; error: string }) => {
    const msg = JSON.stringify({
      type: 'architecture.render-result',
      payload: {
        requestId,
        ok: result.ok,
        ...(result.ok ? {} : { error: result.error }),
      },
    })
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(msg)
    } else {
      outgoingQueueRef.current.push(msg)
    }
  }, [])

  return { sendUIState, sendArchitectureRenderResult }
}
